import {
  buildPath,
  buildTemplatePath,
  CloseButton,
  formatDate,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Typography,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { BulkSelected, SubmitButton } from "components";
import {
  BulkSecondLevelValidation,
  BulkSelectedItemErrors,
  ContributorMatch,
  ContributorMatchBulkUpdate,
  ContributorMatchBulkUpdateResource,
  ContributorMatchBulkUpdateSchema,
} from "generated/openApi";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { MatchUpdateSchema } from "./MatchUpdateSchema";

const routeName = "contributorMatchBulkUpdate";

export const contributorMatchBulkUpdatePageRoute: PageRoute = {
  handle: "Batch Update Time & Effort",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type ContributorMatchBulkUpdatePageState = {
  selectedIds: number[];
};

export const ContributorMatchBulkUpdatePage = () => {
  const dispatch = useDispatch();
  const listContext = useContext<ListContextValue>(ListContext);
  const { state } = useLocation();
  const [stateParam] = useState<ContributorMatchBulkUpdatePageState>(state);
  const [bulkUpdate, setBulkUpdate] = useState<ContributorMatchBulkUpdate>({});
  const [matches, setMatches] = useState<Map<number, ContributorMatch>>(new Map());
  const [resource, setResource] = useState<ContributorMatchBulkUpdateResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [bulkUpdate] });
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [selectedItemsErrors, setSelectedItemsErrors] = useState<BulkSelectedItemErrors[]>([]);

  useEffect(() => {
    getBulkUpdate(stateParam.selectedIds);
    fetchResource();
  }, []);

  const getBulkUpdate = (ids: number[]) => {
    ApiService.contributorMatches.getBulkUpdate(ids).then((response) => {
      setBulkUpdate(response);
      setMatches(new Map(response.matches?.map((x) => [x.id, x])));
      resetUnsavedChanges([response]);
    });
  };

  const fetchResource = () => {
    ApiService.contributorMatches.getBulkUpdateResource().then((response) => {
      setResource(response);
    });
  };

  const save = (bulkUpdate: ContributorMatchBulkUpdate) => {
    setValidationErrors({});
    setSelectedItemsErrors([]);

    ApiService.contributorMatches
      .saveBulkUpdate({ ...bulkUpdate, matches: Array.from(matches.values()) })
      .then((response) => {
        const bulkSecondLevelValidation: BulkSecondLevelValidation | undefined =
          response as unknown as BulkSecondLevelValidation;

        if (bulkSecondLevelValidation && bulkSecondLevelValidation.badRequest) {
          dispatch(setAppToast(newToast("error")));
          setSelectedItemsErrors(bulkSecondLevelValidation.selectedItemsErrors || []);
        } else {
          dispatch(setAppToast(newToast("success")));
          resetUnsavedChanges([bulkUpdate]);
          listContext.loadList?.();
          listContext.clearSelectedIds?.();
        }
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  return (
    <Page
      isForm
      header={
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="inherit">{contributorMatchBulkUpdatePageRoute.handle}</Typography>

          <Stack direction="row" alignItems="center" spacing="sm">
            <CloseButton confirm={unsavedChanges} />
            <SubmitButton
              onClick={() => save(bulkUpdate)}
              disabled={!(bulkUpdate?.matches && bulkUpdate?.matches?.length > 0 && unsavedChanges)}
            />
          </Stack>
        </Stack>
      }
    >
      <Stack spacing="md">
        <MatchUpdateSchema
          updateSchema={bulkUpdate.updateSchema || ({} as ContributorMatchBulkUpdateSchema)}
          setUpdateSchema={(value) => setBulkUpdate({ ...bulkUpdate, updateSchema: value })}
          resource={resource}
          validationErrors={validationErrors?.[pn<ContributorMatchBulkUpdate>("updateSchema")] || []}
        />

        <BulkSelected
          label="Time & Effort"
          items={matches || []}
          setItems={(value) => setMatches(value)}
          getItemLabel={(match) => (
            <Stack>
              <Typography>
                {match.contributor?.lastName}, {match.contributor?.firstName}
              </Typography>
              <Typography variant="caption" lineHeight="100%">
                {formatDate(match.date)}
              </Typography>
            </Stack>
          )}
          errors={selectedItemsErrors}
          setErrors={setSelectedItemsErrors}
        />
      </Stack>
    </Page>
  );
};
